import {
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    ScrollView,
    Platform,
    View
} from "react-native";
import type MailDetailModalProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import {
    type GetSentMailByIdPayload
} from "../../../../services/restAPI/actions/getSentMailById/type";
import moment from "moment";
import RESTServiceContext from "../../../../services/restAPI/context";

const MailDetailModal = ({
    isVisible,
    onClose,
    mailId
}: MailDetailModalProps) => {
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        detail,
        setDetail
    ] = useState<GetSentMailByIdPayload | null>(null);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    useEffect(() => {
        if(!isVisible || !mailId) {
            setDetail(null);
            return;
        }

        const fetchDetail = async () => {
            try {
                setLoading(true);
                const response = await RESTServiceContext.action("getSentMailById", {
                    id: mailId
                });

                if(response?.payload) {
                    setDetail(response.payload);
                }
            } catch(error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchDetail();
    }, [
        isVisible,
        mailId
    ]);

    const renderContent = () => {
        if(loading) {
            return <View
                style={[
                    stylesheet.loadingContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <ActivityIndicator
                    color={colors.content.text.high}
                    size="large"
                />
            </View>;
        }

        if(!detail) {
            return <View
                style={[
                    stylesheet.emptyStateContainer,
                    {
                        padding: spaces.spacingLg
                    }
                ]}
            >
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {localize("notifications-detail-mail-load-error")}
                </Text>
            </View>;
        }

        const isSuccess = detail.status === "success";

        return <ScrollView
            style={stylesheet.contentContainer}
            showsVerticalScrollIndicator={true}
        >
            <View
                style={[
                    stylesheet.metaContainer,
                    {
                        backgroundColor: colors.content.container.subtle,
                        borderRadius: radiuses.sm,
                        padding: spaces.spacingMd,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <View
                    style={stylesheet.metaHeaderRow}
                >
                    <Text
                        customColor={colors.content.text.high}
                        variant="titleMediumSize"
                    >
                        {detail.subject}
                    </Text>
                    <View
                        style={[
                            stylesheet.statusBadge,
                            {
                                backgroundColor: colors.content.container.primary,
                                paddingHorizontal: spaces.spacingSm,
                                paddingVertical: spaces.spacingXs,
                                borderRadius: radiuses.xs
                            }
                        ]}
                    >
                        <Text
                            customColor={colors.content.text.high}
                            variant="bodySmallSize"
                        >
                            {isSuccess ? localize("notifications-table-status-success") : detail.status}
                        </Text>
                    </View>
                </View>
                <View
                    style={stylesheet.metaInfoRow}
                >
                    <Text
                        customColor={colors.content.text.mid}
                        variant="bodySmallSize"
                    >
                        {`${localize("notifications-table-date")}: ${moment(detail.createdAt).format("DD.MM.YYYY HH:mm")}`}
                    </Text>
                    <Text
                        customColor={colors.content.text.mid}
                        variant="bodySmallSize"
                    >
                        {`${localize("notifications-detail-recipients-count")} ${detail.sentCount}`}
                    </Text>
                </View>
                {
                    detail.toMails && detail.toMails.length > 0 ?
                        <View
                            style={[
                                stylesheet.receiversContainer,
                                {
                                    backgroundColor: colors.content.container.primary,
                                    marginTop: spaces.spacingXs,
                                    padding: spaces.spacingSm,
                                    borderRadius: radiuses.xs
                                }
                            ]}
                        >
                            <Text
                                customColor={colors.content.text.mid}
                                variant="bodySmallSize"
                            >
                                {`${localize("notifications-detail-recipients")} (${detail.toMails.length}): ${detail.toMails.slice(0, 10).join(", ")}${detail.toMails.length > 10 ? "..." : ""}`}
                            </Text>
                        </View>
                    :
                        null
                }
            </View>
            <View
                style={[
                    stylesheet.previewContainer,
                    {
                        backgroundColor: colors.content.container.primary,
                        borderColor: colors.content.border.subtle,
                        borderWidth: borders.seperator,
                        marginTop: spaces.spacingMd,
                        borderRadius: radiuses.sm
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.previewHeader,
                        {
                            borderBottomColor: colors.content.border.subtle,
                            borderBottomWidth: borders.seperator,
                            paddingHorizontal: spaces.spacingLg,
                            paddingVertical: spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        customColor={colors.content.text.high}
                        variant="titleSmallSize"
                    >
                        {localize("notifications-detail-preview-title")}
                    </Text>
                </View>
                <View
                    style={stylesheet.previewFrameContainer}
                >
                    {
                        Platform.OS === "web" ?
                            <iframe
                                title={localize("notifications-detail-preview-title")}
                                srcDoc={detail.renderedHtml || detail.content}
                                style={stylesheet.previewIframe}
                            />
                        :
                            <Text
                                customColor={colors.content.text.high}
                                variant="bodyMediumSize"
                            >
                                {detail.content}
                            </Text>
                    }
                </View>
            </View>
        </ScrollView>;
    };

    return <Modal
        portalName="modal-system"
        id="mail-detail-modal"
        isActive={isVisible}
        onClosed={onClose}
    >
        <View
            style={[
                stylesheet.container,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderRadius: radiuses.lg,
                    padding: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.headerContainer,
                    {
                        marginBottom: spaces.spacingLg
                    }
                ]}
            >
                <Text
                    customColor={colors.content.text.high}
                    variant="titleLargeSize"
                >
                    {localize("notifications-detail-mail-title")}
                </Text>
            </View>
            {renderContent()}
            <View
                style={[
                    stylesheet.footerContainer,
                    {
                        marginTop: spaces.spacingMd
                    }
                ]}
            >
                <Button
                    title={localize("notifications-detail-close")}
                    customTextColor={colors.project.brandWhite}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    onPress={onClose}
                    variant="filled"
                />
            </View>
        </View>
    </Modal>;
};
export default MailDetailModal;
